import { useState, useCallback } from 'react'
import { average } from 'lib/array/arrayUtils'
import { getMetresDistanceFromLatLong } from 'lib/flights/getDistanceFromLatLong'
import type { MapPadding } from 'lib/maps/types'
import { getWidth } from 'lib/window/getViewport'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import {
  getLegacySearchBoundsInsetPadding,
  getLegacySearchBoundsTolerancePadding,
  getResponsiveSearchResultsMapPadding,
  searchResultsMapPaddingConfig,
} from './searchBoundsConfig'

type OfferTypes = App.HotelOffer | App.BedbankOffer | App.HotelOfferSummary | App.BedbankOfferSummary | App.OfferListMetaData
type FullOfferTypes = App.HotelOffer | App.BedbankOffer
type OfferWithLocation = FullOfferTypes | App.OfferListMetaData

interface Geolocation {
  longitude: number
  latitude: number
}

function hasLongitudeAndLatitude(location: { longitude?: number, latitude?: number }): boolean {
  return location.longitude !== undefined && location.latitude !== undefined
}

function getGeolocation(offer: OfferWithLocation): Geolocation
function getGeolocation(offer: OfferTypes): Geolocation | undefined
function getGeolocation(offer: OfferTypes): Geolocation | undefined {
  return isOfferListMeta(offer) ? offer.location : offer.property
}

export function isLoadedOffer(offer: OfferTypes): offer is FullOfferTypes {
  return 'id' in offer
}

export function isOfferListMeta(offer: App.AnyOffer | App.OfferListMetaData): offer is App.OfferListMetaData {
  return 'offerId' in offer
}

function hasValidLocation(offer: OfferTypes): offer is OfferWithLocation {
  const propertyOrTour = getGeolocation(offer)
  return !!propertyOrTour && hasLongitudeAndLatitude(propertyOrTour)
}

export function getMarkersByOffers(offers: Array<OfferTypes>, includeOneOfferPerMarker?: boolean): Array<App.SearchMapMarker> {
  const offerGroups = groupOffersAtSameCoordinates(offers.filter(hasValidLocation))
  if (includeOneOfferPerMarker) {
    return offerGroups.map(offers => getMarkerByOfferGroup([offers[0]]))
  }
  return offerGroups.map(offers => getMarkerByOfferGroup(offers))
}

function getMarkerByOfferGroup(offers: Array<OfferWithLocation>): App.SearchMapMarker {
  const latitude = average(offers, offer => getGeolocation(offer).latitude)
  const longitude = average(offers, offer => getGeolocation(offer).longitude)

  return {
    id: offers.map(offer => getOfferId(offer)).join(','),
    latitude: parseFloat((latitude).toFixed(6)),
    longitude: parseFloat((longitude).toFixed(6)),
    offers,
  }
}

export function getOfferId(offer: App.AnyOffer | App.OfferListMetaData): string {
  if (isOfferListMeta(offer)) {
    return offer.offerId
  } else {
    return offer.id
  }
}

export function getDisplayMarkerIdsMatchingOfferIds(
  fullMarkers: Array<App.SearchMapMarker>,
  displayMarkers: Array<App.SearchMapMarker>,
  offerIds: ReadonlySet<string | false | undefined>,
): Set<string> {
  const markerIds = new Set<string>()

  for (let index = 0; index < fullMarkers.length; index++) {
    const displayMarkerId = displayMarkers[index]?.id

    if (!displayMarkerId) {
      continue
    }

    if (fullMarkers[index].offers.some((offer) => offerIds.has(getOfferId(offer)))) {
      markerIds.add(displayMarkerId)
    }
  }

  return markerIds
}

export function getBoundsDisambiguationCoordinates(
  markers: Array<App.SearchMapMarker>,
  offerListBounds?: App.OfferListBounds,
): Array<{ lat: number, lng: number }> {
  const markerCoordinates = markers.map(marker => ({
    lat: marker.latitude,
    lng: marker.longitude,
  }))

  if (!offerListBounds) {
    return markerCoordinates
  }

  return [
    ...markerCoordinates,
    {
      lat: +offerListBounds.southwest.lat,
      lng: +offerListBounds.southwest.lng,
    },
    {
      lat: +offerListBounds.northeast.lat,
      lng: +offerListBounds.northeast.lng,
    },
  ]
}

/*
  Groups offers that either have the same property, or are in the same building, or otherwise are so
  close together that it's not feasible to show separate markers for them.
  To make this algorithm more efficient, first the offers are sorted by their latitude.
  That means for each offer, instead of comparing it against every other offer (which would have
  O(n^2) time complexity), we can stop comparing once we reach an offer that's too far away in
  latitude - each subsequent offer will also be too far away, so we can break that loop and move on.
*/
export function groupOffersAtSameCoordinates(offers: Array<OfferWithLocation>): Array<Array<OfferWithLocation>> {
  const metresThreshold = 10
  const groupedOfferSet = new Set<OfferWithLocation>()
  const groups: Array<Array<OfferWithLocation>> = []

  for (let i = 0; i < offers.length; i++) {
    const referenceOffer = offers[i]

    if (groupedOfferSet.has(referenceOffer)) {
      continue
    }

    const { latitude: lat1, longitude: lon1 } = getGeolocation(referenceOffer)

    const group = [referenceOffer]
    groupedOfferSet.add(referenceOffer)

    // Find offers that are within the threshold distance from `referenceOffer`
    for (let j = i + 1; j < offers.length; j++) {
      const thisOffer = offers[j]
      const { latitude: lat2, longitude: lon2 } = getGeolocation(thisOffer)

      if (groupedOfferSet.has(thisOffer)) {
        continue // Skip offers already in a group
      }

      if (getMetresDistanceFromLatLong(lat1, lon1, lat2, lon2) < metresThreshold) {
        group.push(thisOffer)
        groupedOfferSet.add(thisOffer)
      }
    }

    groups.push(group)
  }

  return groups
}

export function getPadding(fudged: boolean = false) {
  return getResponsiveSearchResultsMapPadding(
    fudged ? searchResultsMapPaddingConfig.fitBoundsFudged : searchResultsMapPaddingConfig.fitBounds,
  )
}

/**
 * Legacy flat-map inset used by shared Google Maps consumers.
 * Keep globe-specific zoom-aware logic in `searchBoundsConfig.ts`.
 */
export function getPaddingForBounds(): MapPadding {
  return getLegacySearchBoundsInsetPadding()
}

/**
 * Legacy flat-map tolerance used by shared Google Maps consumers.
 * Keep globe-specific zoom-aware logic in `searchBoundsConfig.ts`.
 */
export function getSearchBoundsTolerancePadding(): MapPadding {
  return getLegacySearchBoundsTolerancePadding()
}

/**
 * handle cross 180th meridian case when calculate longitude span
 * when a rectangle cross the 180th meridian, the longitude on east side goes negative
 * while the west side remains positive
 */
function getLongitudeSpan(eastLong: number, westLong: number): number {
  if (eastLong < 0 && westLong > 0) {
    return (180 - westLong) + (180 + eastLong)
  }

  return eastLong - westLong
}

/**
 * Gives us the map bounds within the padding, so we can search for offers without them appearing too
 * close to the edge of the map.
 */
export function subtractPaddingFromBounds(bounds: google.maps.LatLngBounds, boundingRect: DOMRect) {
  const padding = getPaddingForBounds()

  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()
  const longitudeSpan = getLongitudeSpan(ne.lng(), sw.lng())
  const latitudeSpan = ne.lat() - sw.lat()

  const longitudeDegreesPerPixel = longitudeSpan / boundingRect.width
  const latitudeDegreesPerPixel = latitudeSpan / boundingRect.height

  const paddingTop = latitudeDegreesPerPixel * padding.top
  const paddingBottom = latitudeDegreesPerPixel * padding.bottom
  const paddingLeft = longitudeDegreesPerPixel * padding.left
  const paddingRight = longitudeDegreesPerPixel * padding.right

  return new google.maps.LatLngBounds(
    new google.maps.LatLng({ lat: sw.lat() + paddingBottom, lng: sw.lng() + paddingLeft }),
    new google.maps.LatLng({ lat: ne.lat() - paddingTop, lng: ne.lng() - paddingRight }),
  )
}

// over 480 - the desktop version of the fitted map looks good, less than 480 - a lot of grey space on top and under the map
const MAX_MOBILE_WIDTH = 480

export function fitMarkersOnMap(map: google.maps.Map | null, markers: Array<App.SearchMapMarker | App.SearchExperienceMapMarker>, centerMarker?: App.SearchMapMarker | App.SearchExperienceMapMarker) {
  if (!map || markers.length === 0) {
    return
  }

  if (markers.length === 1) {
    map.setCenter(new google.maps.LatLng(markers[0].latitude, markers[0].longitude))
    map.setZoom(15)
    return
  }

  const bounds = new google.maps.LatLngBounds()

  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()

  if (!ne || !sw) {
    return
  }

  markers.forEach(marker => {
    bounds.extend(new google.maps.LatLng(marker.latitude, marker.longitude))
  })

  const longitudeSpan = ne.lng() - sw.lng()

  const viewportWidth = getWidth()

  if (viewportWidth <= MAX_MOBILE_WIDTH && longitudeSpan > 100) {
    if (centerMarker) {
      map.setCenter(new google.maps.LatLng(centerMarker.latitude, centerMarker.longitude))
      const mapDiv = map.getDiv()
      if (mapDiv) {
        const height = mapDiv.offsetHeight
        const listener = google.maps.event.addListener(map, 'idle', () => {
          map.panBy(0, height / 3)
          google.maps.event.removeListener(listener)
        })
      }
    } else {
      map.setCenter(new google.maps.LatLng(markers[0]?.latitude, markers[0]?.longitude))
    }
    map.setZoom(1)
  } else {
    map.fitBounds(bounds, getPadding(false))

    if (centerMarker) {
      const listener = google.maps.event.addListener(map, 'idle', () => {
        map.setCenter(new google.maps.LatLng(centerMarker.latitude, centerMarker.longitude))
        if (viewportWidth <= MAX_MOBILE_WIDTH) {
          const mapDiv = map.getDiv()
          if (mapDiv) {
            const height = mapDiv.offsetHeight
            map.panBy(0, height / 3)
          }
        }
        google.maps.event.removeListener(listener)
      })
    }
  }
}

export function centerAndZoomOnMarker(map: google.maps.Map | null, marker: App.SearchMapMarker, shouldZoom: boolean) {
  if (map) {
    map.setCenter(new google.maps.LatLng(marker.latitude, marker.longitude))
    if (shouldZoom) {
      map.setZoom(16)
    }
  }
}

export function centrePinForMobile(map: google.maps.Map | null, marker: App.SearchMapMarker) {
  if (!map) {
    return
  }

  const latLng = new google.maps.LatLng(marker.latitude, marker.longitude)
  map.panTo(latLng)

  const mapDiv = map.getDiv()
  if (!mapDiv) return
  const mapHeight = mapDiv.offsetHeight

  const percentFromTop = 0.25
  const targetPosition = mapHeight * percentFromTop
  const offsetRatio = (mapHeight / 2 - targetPosition) / mapHeight

  map.panBy(0, offsetRatio * mapHeight)
}

export function parseBounds(bounds: string) {
  const [latLo, lngLo, latHi, lngHi] = bounds.split(',')
  return new google.maps.LatLngBounds(
    new google.maps.LatLng(+latLo, +lngLo),
    new google.maps.LatLng(+latHi, +lngHi),
  )
}

export function parseOfferListBounds(bounds: App.OfferListBounds) {
  return new google.maps.LatLngBounds(
    new google.maps.LatLng(+bounds.southwest.lat, +bounds.southwest.lng), // SW corner first
    new google.maps.LatLng(+bounds.northeast.lat, +bounds.northeast.lng), // NE corner second
  )
}

export function mapHasAnyMarkersInView(map: google.maps.Map | null, markers: Array<App.SearchMapMarker>): boolean {
  const bounds = map?.getBounds()
  if (!bounds) { return false }
  return markers.some(marker => bounds.contains(new google.maps.LatLng(marker.latitude, marker.longitude)))
}

const vmStorageKey = 'searchMapVisitedMarkers'
export function useVisitedMarkersState() {
  const [visitedMarkerIds, setVisitedMarkerIds] = useState(() => {
    const storedIdsRaw = isomorphicSessionStorage.get(vmStorageKey)
    return new Set<string>(storedIdsRaw ? JSON.parse(storedIdsRaw) : [])
  })

  const addVisitedMarkerId = useCallback((markerId: string) => {
    setVisitedMarkerIds(ids => {
      if (ids.has(markerId)) { return ids }
      const newIds = new Set(ids)
      newIds.add(markerId)
      isomorphicSessionStorage.set(vmStorageKey, JSON.stringify(Array.from(newIds)))
      return newIds
    })
  }, [])

  return [visitedMarkerIds, addVisitedMarkerId] as const
}

export function isMarkerWithinBounds(marker: App.SearchMapMarker, bounds: google.maps.LatLngBounds): boolean {
  const markerBounds = new google.maps.LatLngBounds(
    new google.maps.LatLng(marker.latitude, marker.longitude),
    new google.maps.LatLng(marker.latitude, marker.longitude),
  )
  return bounds.intersects(markerBounds)
}

export function areOffersBedbank(
  offers: Array<App.Offer | App.BedbankOffer | App.OfferListMetaData>,
): offers is Array<App.BedbankOffer> {
  return offers.some(offer => offer.type === OFFER_TYPE_BED_BANK)
}
